/**
 * Unified Connectivity API — single source of truth for connectivity decisions.
 *
 * All application layers (boot, authentication restore, feature gating, sync)
 * must use this module instead of raw navigator.onLine or ad-hoc offline checks.
 *
 * State machine implementation: connectivityManager.js
 */

import {
  CONNECTIVITY_CHANGED_EVENT,
  CONNECTIVITY_STATE,
  TRANSPORT_TYPE,
  canAttemptSync as canAttemptSyncFromManager,
  getActiveTransport,
  getConnectivitySnapshot,
  getConnectivityState,
  isEffectivelyOffline as isEffectivelyOfflineFromManager,
  isGoodConnection,
  reportConnectivityFailure,
  reportConnectivitySuccess,
  startConnectivityManager,
  stopConnectivityManager,
  subscribeConnectivity,
} from "./connectivityManager";

export {
  CONNECTIVITY_CHANGED_EVENT,
  CONNECTIVITY_STATE,
  TRANSPORT_TYPE,
  getActiveTransport,
  getConnectivitySnapshot,
  getConnectivityState,
  reportConnectivityFailure,
  reportConnectivitySuccess,
  startConnectivityManager,
  stopConnectivityManager,
  subscribeConnectivity,
};

/**
 * Browser-reported link status only. Prefer semantic APIs below for app decisions.
 */
export function isBrowserReportedOnline() {
  return typeof navigator === "undefined" || navigator.onLine;
}

/**
 * True when the app should use offline/local-first behavior.
 * Includes browser-offline and poor (unstable) connectivity.
 */
export function isEffectivelyOffline() {
  return isEffectivelyOfflineFromManager();
}

/** Alias for feature modules — same semantics as isEffectivelyOffline(). */
export function isOffline() {
  return isEffectivelyOffline();
}

/** True when connectivity quality is good (remote reads and sync allowed). */
export function isConnectivityHealthy() {
  return isGoodConnection();
}

/**
 * True when pending Supabase writes may be synced.
 * Requires browser online and connectivity state good.
 */
export function canAttemptSync() {
  return canAttemptSyncFromManager();
}

/**
 * True when cached authentication may be restored without a live Supabase session.
 * Uses the same offline gate as feature modules.
 */
export function canRestoreCachedAuthentication() {
  return isEffectivelyOffline();
}
